import {SettingParam} from "@/types/node";
import {useEffect, useState} from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {Input} from "../ui/input";
import {AuthValue} from "@/types/node";
import {AuthorizationTypeEnum} from "@/api/models/actions/customAction";
import GoHighLevel from "@/components/icons/GoHightLevel";
import HubspotIcon from "@/components/icons/Hubspot";
import SalesforceMinIcon from "@/components/icons/SalesforceMin";
import {useCheckProviderApiKey} from "@/api/services/queries";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";

const AUTHENTICATION_TYPES = [
  {
    name: "No Auth",
    value: "no_auth",
  },
  {
    name: "Authentication",
    value: "authentication",
  },
];

const Authentication = ({
  value,
  onChange,
  disabled = false,
}: {
  value: AuthValue["data"];
  onChange: (value: AuthValue["data"]) => void;
  disabled: boolean;
}) => {
  const [auth, setAuth] = useState(value);

  useEffect(() => {
    auth.type = "authentication";
    auth.location = "header";

    onChange(auth);
  }, [auth]);

  return (
    <div className="mt-4 w-full">
      <div className="mb-4 w-full">
        <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
          Authentication Key
        </label>

        <Input
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:bg-muted`}
          onChange={e => {
            setAuth({...auth, key: e.target.value});
          }}
          value={auth.key || ""}
          placeholder="Authorization, API-Key, or x-api-key"
          disabled={disabled}
        />
      </div>
      <div className="mb-4 w-full">
        <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
          Authentication Value
        </label>

        <Input
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:bg-muted`}
          onChange={e => {
            setAuth({...auth, value: e.target.value});
          }}
          value={auth.value || ""}
          placeholder="Bearer <token> or your-api-key"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const IntegrationAuthentication = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: AuthValue) => void;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const authTypeMap = {
    ghl_auth: AuthorizationTypeEnum.GoHighLevel,
    hubspot_auth: AuthorizationTypeEnum.Hubspot,
    salesforce_auth: AuthorizationTypeEnum.Salesforce,
  };

  const mapAuthEnumToType = (authType: AuthorizationTypeEnum): string => {
    const authEnumToTypeMap: Record<AuthorizationTypeEnum, string> = {
      [AuthorizationTypeEnum.Custom]: "",
      [AuthorizationTypeEnum.GoHighLevel]: "ghl",
      [AuthorizationTypeEnum.Hubspot]: "hubspot",
      [AuthorizationTypeEnum.Salesforce]: "salesforce",
    };
    return authEnumToTypeMap[authType] || "";
  };
  const authType = authTypeMap[param.key as keyof typeof authTypeMap];
  const {isSuccess: isConnected} = useCheckProviderApiKey(
    FlowBuilderSettings.workspaceId,
    mapAuthEnumToType(authType),
    {
      query: {
        enabled: authType !== AuthorizationTypeEnum.Custom,
      },
    },
  );

  const mapAuthEnumToName = (authType: AuthorizationTypeEnum): string => {
    const authEnumToNameMap: Record<AuthorizationTypeEnum, string> = {
      [AuthorizationTypeEnum.Custom]: "",
      [AuthorizationTypeEnum.GoHighLevel]: "GoHighLevel",
      [AuthorizationTypeEnum.Hubspot]: "Hubspot",
      [AuthorizationTypeEnum.Salesforce]: "Salesforce",
    };
    return authEnumToNameMap[authType] || "";
  };

  const AuthTypeIconMap: Record<string, React.ElementType> = {
    [AuthorizationTypeEnum.GoHighLevel]: GoHighLevel,
    [AuthorizationTypeEnum.Hubspot]: HubspotIcon,
    [AuthorizationTypeEnum.Salesforce]: SalesforceMinIcon,
  };
  const authName = mapAuthEnumToName(authType);
  const IconComponent = AuthTypeIconMap[authType];

  return (
    <div>
      <div className="flex">
        <div className="flex gap-4">
          <div className="flex justify-center shrink-0 items-center w-10 h-10 rounded-md border border-border">
            {IconComponent && <IconComponent width={30} height={30} />}
          </div>
          <div>
            <p className="text-label text-sm font-medium">{authName}</p>
            {!isConnected ? (
              <p className="text-secondary text-sm font-medium">
                To connect your {authName} account, please go to the
                integrations page
              </p>
            ) : (
              <p className="text-secondary text-sm font-medium">Connected</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SettingAuthenticate = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: AuthValue) => void;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const [value, setValue] = useState<AuthValue>(
    (param.value as AuthValue) || {},
  );

  useEffect(() => {
    handleChange(param, value);
  }, [value]);

  return (
    <div className="mb-4 w-full">
      <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
        {param.label}
      </label>
      {param.key !== "custom_auth" ? (
        <IntegrationAuthentication
          param={param}
          handleChange={handleChange}
          selectedNodeId={selectedNodeId}
          disabled={disabled}
        />
      ) : (
        <>
          <Select
            onValueChange={authType => {
              const authDataKeys = {
                no_auth: {},
                authentication: {key: "", value: ""},
              };

              setValue({
                authType: authType,
                data: authDataKeys[authType as keyof typeof authDataKeys],
              });
            }}
            value={value.authType || AUTHENTICATION_TYPES[0].value}
            disabled={disabled}
          >
            <SelectTrigger className={disabled ? "pointer-events-none" : ""}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {AUTHENTICATION_TYPES.map(type => (
                <SelectItem value={type.value} key={type.value}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {value.authType === "authentication" && (
            <Authentication
              value={value.data}
              onChange={data => {
                setValue({...value, data});
              }}
              disabled={disabled}
            />
          )}
        </>
      )}
    </div>
  );
};
